import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import type { ClientId } from '@seaber/common/lib/clientId';
import type {
  ScenarioTimeline_OrderFragment,
  UpdateVoyageCharterOrderFreightMutationResult,
  UpdateVoyageCharterOrderFreightMutationVariables,
} from '../../../../graphql';
import { UpdateVoyageCharterOrderFreightDocument } from '../../../../graphql';
import type { useApolloClient } from '@apollo/client';

const applyFreightRateOverridesToVC = async ({
  estimateId,
  scenarios,
  orderById,
  client,
}: {
  estimateId: ClientId;
  scenarios: ScenarioSchedule[];
  orderById: Map<number, ScenarioTimeline_OrderFragment>;
  client: ReturnType<typeof useApolloClient>;
}) => {
  const scenarioWithEstimate = scenarios.find((s) =>
    s.estimateTimelines?.some((e) => e.clientId === estimateId),
  );
  const estimate = scenarioWithEstimate?.estimateTimelines?.find(
    (e) => e.clientId === estimateId,
  );
  const freightRateOverrides = estimate?.freightRateOverridesByOrderId;
  if (!freightRateOverrides || freightRateOverrides.size === 0) {
    return;
  }

  const updateVCOrderFreights: Array<Promise<unknown>> = [];

  freightRateOverrides.forEach((override, orderId) => {
    const { freight: freightAmount, freightUnit } = override;

    const vcRel = orderById.get(orderId)?.incomingVoyageCharterRelation;
    if (!vcRel) return;

    const existingFreight =
      vcRel.voyageCharterOrderFreightsByOrderVoyageCharterRelationId.nodes.at(
        0,
      );

    if (!existingFreight) {
      return;
    }

    updateVCOrderFreights.push(
      client.mutate<
        UpdateVoyageCharterOrderFreightMutationResult['data'],
        UpdateVoyageCharterOrderFreightMutationVariables
      >({
        mutation: UpdateVoyageCharterOrderFreightDocument,
        variables: {
          input: {
            id: existingFreight.id,
            voyageCharterOrderFreightPatch: {
              freightAmount,
              freightUnit,
            },
          },
        },
      }),
    );
  });

  if (updateVCOrderFreights.length > 0) {
    await Promise.all(updateVCOrderFreights);
  }
};

export default applyFreightRateOverridesToVC;
